import { Component, Input, OnInit, Inject, Output, EventEmitter, HostBinding } from '@angular/core'
import { FileItem, FileLikeObject, FileUploader } from 'ng2-file-upload'
import { FileSizePipe } from '../../pipes'
import { FileUploaderFactoryService, Toaster, CustomFileUploaderOptions } from '../../services'
import { ENVIRONMENT } from '../../tokens'
import { setUploadingAttachments } from '../../stores/attachment/attachment.actions'
import { Store } from '@ngrx/store'

export interface UploadBoxOptions {
  uploadUrl?: string
  maxSize?: number
  maxUploads?: number
  autoUpload?: boolean
  allowedFileExtensions?: string[]
  guide?: string
  cloudinary?: boolean
  token?: string
  image?: string
  resetAfterFailed?: boolean // default is true
  resetAfterCompleted?: boolean // default is true
  emitUploadingEvent?: boolean // default is true
}

export const defaultUploadBoxOptions: UploadBoxOptions = {
  maxSize: 10 * 1024 * 1024, // 10MB,
  maxUploads: 1,
  autoUpload: false,
  cloudinary: false,
}

@Component({
  selector: 'app-upload-box',
  templateUrl: './UploadBox.component.html',
  styleUrls: ['./UploadBox.component.scss'],
})
export class UploadBoxComponent implements OnInit {
  @Input() options: UploadBoxOptions = defaultUploadBoxOptions

  _uploadedCount = 0
  @Input() set uploadedCount(count: number) {
    this._uploadedCount = count
    if (this.uploader && this.options.maxUploads > 0) {
      this.uploader.setOptions({ ...this.uploader.options, queueLimit: Math.max(this.options.maxUploads - count, 0) })
    }
  }

  @Input() image: string
  @Input() imageHeight: string = '80px'

  @Input() showFileList = true
  @Input() showUploadingBar = true

  @HostBinding('class')
  @Input()
  direction: 'v' | 'h' = 'h'

  @Output() complete = new EventEmitter<any[]>()
  @Output() afterAddingAll = new EventEmitter<FileItem[]>()
  @Output() afterAddingFile = new EventEmitter<FileItem>()
  @Output() afterFileUploaded = new EventEmitter<FileItem>()
  @Output() afterFileError = new EventEmitter<FileItem>()
  @Output() addingFileFailed = new EventEmitter<FileLikeObject>()
  @Output() completeItem = new EventEmitter<any>()

  get isUploading() {
    return this.uploader?.isUploading
  }

  get isPendingEmpty() {
    return !this.uploader?.queue?.length
  }

  uploader: FileUploader

  files: FileItem[] = []
  uploadedFileResults: any[] = []

  acceptFileExtensions: string

  private fileSizePipe = new FileSizePipe()
  constructor(
    private fileUploaderFactory: FileUploaderFactoryService,
    @Inject(ENVIRONMENT) private environment,
    protected toast: Toaster,
    private store: Store<any>
  ) {}

  ngOnInit(): void {
    if (!this.options.allowedFileExtensions && this.options.cloudinary) {
      this.options.allowedFileExtensions = ['.png', '.jpg', '.jpeg', '.jfif']
    }

    if (this.options.allowedFileExtensions) {
      this.acceptFileExtensions = this.options.allowedFileExtensions.join(',')
    }

    this.uploader = this.fileUploaderFactory.create(
      this.options.cloudinary ? this.getCloudinaryUploadOptions() : this.getTokeetUploadOptions(),
      {
        onAfterAddingFile: async (fileItem) => {
          if (this.options.cloudinary) {
            fileItem.withCredentials = false
          }
          this.files.push(fileItem)
          this.afterAddingFile.emit(fileItem)
        },
        onAfterAddingAll: (fileItems) => {
          this.afterAddingAll.emit(fileItems)
        },
        onBeforeUploadItem: (fileItem) => {
          if (this.options.emitUploadingEvent !== false) {
            this.store.dispatch(setUploadingAttachments())
          }
        },
        onCompleteItem: (fileItem, res, status, headers) => {
          if (status > 299) return // ignore errors
          this.afterFileUploaded.emit(fileItem)
          let resObj
          try {
            resObj = JSON.parse(res)
            this.uploadedFileResults = this.uploadedFileResults.concat(resObj)
          } catch (e) {}
          this.completeItem.emit(resObj || res)
        },
        onWhenAddingFileFailed: (item, filter, options) => {
          this.addingFileFailed.emit(item)
        },
        onErrorItem: (item, response, status, headers) => {
          this.afterFileError.emit(item)
          if (this.options.resetAfterFailed !== false) {
            this.uploader.removeFromQueue(item)
          }
          this.toast.error($localize`Unable to upload file`, '', !status ? null : JSON.parse(response))
        },
        onCompleteAll: () => {
          if (this.uploadedFileResults.length) {
            this.complete.emit(this.uploadedFileResults)
          }

          if (this.options.resetAfterCompleted !== false) {
            this.uploader.clearQueue()
            this.uploadedFileResults = []
            this.files = []
          }
        },
      }
    )
  }

  getTokeetUploadOptions(): CustomFileUploaderOptions {
    const options: CustomFileUploaderOptions = {
      url: this.options.uploadUrl || `${this.environment.apiUrl}/attachment`,
      autoUpload: this.options.autoUpload,
      maxFileSize: this.options.maxSize,
      allowedFileExtensions: this.options.allowedFileExtensions,
      authToken: this.options.token,
    }
    if (this.options.maxUploads > 0) {
      options.queueLimit = Math.max(this.options.maxUploads - this._uploadedCount, 0)
    }
    return options
  }

  getCloudinaryUploadOptions(): CustomFileUploaderOptions {
    const options: CustomFileUploaderOptions = {
      url: `${this.environment.config.cloudinaryURL}/${this.environment.config.cloudinaryCloudName}/upload`,
      allowedFileExtensions: this.options.allowedFileExtensions,
      autoUpload: this.options.autoUpload,
      maxFileSize: this.options.maxSize,
      additionalParameter: {
        upload_preset: this.environment.config.cloudinaryUploadPreset,
        tags: 'ablum',
        context: 'photo=' + ('title' || ''),
      },
    }

    if (this.options.maxUploads > 0) {
      options.queueLimit = Math.max(this.options.maxUploads - this._uploadedCount, 0)
    }

    return options
  }

  getGuideText() {
    if (this.options.guide) {
      return this.options.guide
    }
    const maxUploads = this.options.maxUploads ?? 'multiple'
    const size = this.fileSizePipe.transform(this.options.maxSize, 0)
    return (
      $localize`You can upload` +
      ` <b>${maxUploads}</b> ` +
      $localize`file(s) at one time and file size should be less than` +
      ` <b>${size}</b>.`
    )
  }

  reset() {
    this.uploader.clearQueue()
  }

  onRemoveFile(file: FileItem, index: number) {
    this.uploader.removeFromQueue(file)
    this.files.splice(index, 1)
  }

  upload() {
    this.uploader.uploadAll()
  }
}
